import React, { useEffect, useState } from "react"
import { FormattedMessage, FormattedNumber, injectIntl, defineMessages } from "react-intl"
import Tab from "react-bootstrap/Tab"
import Tabs from "react-bootstrap/Tabs"
import SideBar from "../../common/layout/sidebar.component"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import FeeService from "../../state/fee/FeeService"
import { isError } from "../../state/helpers/Error"
import Fee from "../../state/fee/Fee"
import StandardList from "../../common/listing/list.component"
import {
    Link,
} from "react-router-dom"
import IntlProps from "../../state/helpers/IntlProps"
import MyAccountTabs from "../../common/layout/tabs/myaccount.component"
import Icon from "../../common/icon/icon.component"
import BetaFeatureIcon from "../../common/beta-feature-components/BetaIconWithToolTip"
import { TYPE_LATE_FEE, TYPE_REGULAR_FEE } from "../../state/fee/feeTypes"
import { Button } from "react-bootstrap"

const tabLabels =
    {
        [TYPE_REGULAR_FEE]: <FormattedMessage
            id="fee.tab.fees"
            defaultMessage="Fees"
        />,
        [TYPE_LATE_FEE]: <FormattedMessage
            id="fee.tab.late_fees"
            defaultMessage="Late Fees"
        />,
    }

const messages = defineMessages({
    "rate": {
        id: "fee.form.listing.th.rate_amount",
        defaultMessage: "Rate / Amount",
    },

    "feeName": {
        id: "fee.form.listing.th.name",
        defaultMessage: "Name",
    },
    "action": {
        id: "fee.form.listing.th.action",
        defaultMessage: "Action",
    },

    "default_badge": {
        id: "fee.form.listing.default_label",
        defaultMessage: "Default",
    }

})

type OwnProps = {
    className: string,
    fees: [],
}

const FeeSection = (props: OwnProps & IntlProps) => {

    //get url params
    const search = window.location.search
    const query = new URLSearchParams(search)
    const feeType = query.get("feeType") as string

    const [fees, setFees] = useState<Fee[]>([])
    const [key, setKey] = useState<string>(feeType !== null ? feeType : TYPE_REGULAR_FEE)
    const [isLoading, setLoading] = useState(true)

    useEffect(() => {
        (async () => {
            const r = await FeeService.get()
            if (!isError(r)) {
                setFees(r as Fee[])
                setLoading(false)
            }
        })()
        SideBar.activateMenu("account")
    }, [])

    const getFees = async (): Promise<Fee[]> => {
        const r = await FeeService.get()
        if (!isError(r)) {
            return r as Fee[]
        }
        return [] as Fee[]
    }

    const formatRateDisplay = (fee: Fee) => {
        switch (fee.getRateType()) {
        case "flat-rate":
            return (
                <FormattedNumber
                    style={"currency"}
                    currency={AppContext.getCurrency()}
                    maximumFractionDigits={2}
                    value={parseFloat(fee.getRate())}/>
            )
        case "percentage-rate":
        default:
            return (
                <FormattedNumber
                    style={"percent"}
                    maximumFractionDigits={2}
                    value={parseFloat(fee.getRate()) / 100}
                />
            )
        }
    }

    const setAsDefault = async (feeId: string) => {
        const r = await FeeService.setFeeAsDefault(feeId, key)
        if (!isError(r)) {
            const fees = await getFees()
            setFees([...fees])
        }
    }

    const removeDefault = async (feeId: string) => {
        const r = await FeeService.update({isDefault: false}, feeId)
        if (!isError(r)) {
            const fees = await getFees()
            setFees([...fees])
        }
    }

    const getHeaders = () => {
        return [
            props.intl.formatMessage(messages.feeName),
            props.intl.formatMessage(messages.rate),
            props.intl.formatMessage(messages.action),
        ]
    }

    const getRow = (fee: Fee) => {
        let defaultBadge = <></>
        if (fee.isDefault()) {
            defaultBadge =
                <span id="inactive_badge" className="badge badge-info">
                    {props.intl.formatMessage(messages.default_badge)}
                </span>

        }

        let defaultButton =
            <Button onClick={() => setAsDefault(fee.getId())} variant="outline-primary" size="sm"
                className="inactive_badge">
                <FormattedMessage
                    id="fee.form.listing.th.default_btn"
                    defaultMessage="Set default"
                />
            </Button>

        if (fee.isDefault()) {
            defaultButton = <Button onClick={() => removeDefault(fee.getId())} variant="outline-primary" size="sm">
                <FormattedMessage
                    id="fee.form.listing.th.remove_default_btn"
                    defaultMessage="Remove default"
                />
            </Button>
        }

        return [
            <Link key={0} to={`/settings/fees/${fee.getId()}/edit?feeType=${key}`}>{fee.getName()} &nbsp;{
                <span>{defaultBadge}</span>}</Link>,
            <div key={1} className="feeRate">{formatRateDisplay(fee)}</div>,
            defaultButton,
        ]
    }

    return (
        <div>
            <MyAccountTabs/>
            <div className="row ui-has-modules py-1">
                <div className="col-12 ui-module mb-0">
                    <div className="row mb-0 justify-content-end">
                        <div className="col-3 text-align-right ">
                            <Link to={`/settings/fees/new?create_type=${key}`} className="btn btn-primary">
                                <Icon value={"plus-circle"}/>
                                <FormattedMessage
                                    id="fee.form.listing.add_fee_btn"
                                    defaultMessage="Create fee"/>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            {!isLoading &&
                <Tabs
                    activeKey={key}
                    onSelect={(k) => {
                        if (k) {
                            setKey(k)
                        }

                        //update url params on tab click
                        query.set("feeType", k as string)
                        const newRelativePathQuery = window.location.pathname + "?" + query.toString()
                        history.replaceState(null, "", newRelativePathQuery)
                    }}
                    className="mt-2"
                >
                    <Tab eventKey={TYPE_REGULAR_FEE} title={tabLabels[TYPE_REGULAR_FEE]}>
                        <StandardList
                            className="row pt-0"
                            headers={getHeaders()}
                            rows={fees.filter(f => !f.isLateFee()).map(r => getRow(r))}
                            smallLine={true}
                            headerClassName={"text-white"}
                        />
                    </Tab>
                    <Tab
                        eventKey={TYPE_LATE_FEE}
                        title={
                            <span>
                                <BetaFeatureIcon/>
                                {tabLabels[TYPE_LATE_FEE]}
                            </span>
                        }>
                        <StandardList
                            className="row pt-0"
                            headers={getHeaders()}
                            rows={fees.filter(f => f.isLateFee()).map(r => getRow(r))}
                            smallLine={true}
                        />
                    </Tab>
                </Tabs>

            }
        </div>
    )
}

export default injectIntl(FeeSection)



// WEBPACK FOOTER //
// ./src/settings/fees/FeeSection.tsx